/**
 * Username dropdown helpers — Preferences, Help, About, Send Feedback modals
 * plus the runtime applier that pushes the saved prefs onto the document root.
 *
 * Preferences persist via the same Prefs / Settings cloud sync everything else
 * uses; the applier just reads them and sets data-attributes on <html>, which
 * the CSS variables and font-size rules in styles.css respond to.
 */
import { Settings } from "../lib/prefs";
import { escapeHtml, toast, $ } from "../lib/dom";
import { openModal, closeModal } from "./modal";
import { SECTIONS, render } from "../state";
import { sb, CLOUD_USER } from "../lib/supabase";

/* ---------- Apply persisted prefs to the document root ---------- */

export function applyUserPrefs(): void {
  const s = Settings.get();
  const root = document.documentElement;
  root.setAttribute("data-theme",     s.theme || "dark");
  root.setAttribute("data-text-size", s.textSizeScale || "md");
}

/* ---------- Preferences modal ---------- */

const TEXT_SIZE_OPTS: Array<{ value: string; label: string }> = [
  { value: "sm", label: "Small (14px base)" },
  { value: "md", label: "Default (16px base)" },
  { value: "lg", label: "Large (18px base)" },
  { value: "xl", label: "Extra Large (20px base)" },
];

export function openPreferences(): void {
  const s = Settings.get();
  // Landing page options are every navigable section.
  const landingOpts = SECTIONS.map(sec => ({ value: sec.id, label: sec.label }));

  const body = `
    <div class="space-y-4">
      <div class="panel-2 p-3">
        <h4 class="font-semibold text-sm mb-2 text-emerald-300">General</h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="label" for="prefLandingPage">Default landing page</label>
            <select class="select" id="prefLandingPage">
              ${landingOpts.map(o => `<option value="${escapeHtml(o.value)}" ${s.defaultLandingPage === o.value ? "selected" : ""}>${escapeHtml(o.label)}</option>`).join("")}
            </select>
            <div class="text-xs text-[var(--muted)] mt-1">The section that opens first on sign-in (when no URL hash is set).</div>
          </div>
        </div>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold text-sm mb-2 text-emerald-300">Accessibility</h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="label" for="prefTheme">Theme</label>
            <select class="select" id="prefTheme">
              <option value="dark"  ${s.theme === "dark"  ? "selected" : ""}>Dark</option>
              <option value="light" ${s.theme === "light" ? "selected" : ""}>Light</option>
            </select>
            <div class="text-xs text-[var(--muted)] mt-1">Surfaces, text, and borders flip between dark and light palettes. Accent colors stay the same.</div>
          </div>
          <div>
            <label class="label" for="prefTextSize">Text size</label>
            <select class="select" id="prefTextSize">
              ${TEXT_SIZE_OPTS.map(o => `<option value="${escapeHtml(o.value)}" ${s.textSizeScale === o.value ? "selected" : ""}>${escapeHtml(o.label)}</option>`).join("")}
            </select>
            <div class="text-xs text-[var(--muted)] mt-1">Scales the whole app's font size. Best paired with a comfortable browser zoom.</div>
          </div>
        </div>
      </div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="savePreferences()">Save</button>`;
  openModal({ title: "Preferences", body, footer });
}

function savePreferences(): void {
  const landing = ($("#prefLandingPage") as HTMLSelectElement | null)?.value || "dashboard";
  const theme   = ($("#prefTheme")       as HTMLSelectElement | null)?.value as "dark" | "light";
  const size    = ($("#prefTextSize")    as HTMLSelectElement | null)?.value as "sm" | "md" | "lg" | "xl";
  Settings.set({
    defaultLandingPage: landing,
    theme: theme === "light" ? "light" : "dark",
    textSizeScale: ["sm", "md", "lg", "xl"].includes(size) ? size : "md",
  });
  applyUserPrefs();
  toast("Preferences saved");
  closeModal();
  render();
}

/* ---------- Help / keyboard shortcuts modal ---------- */

export function openHelp(): void {
  const body = `
    <div class="space-y-4 text-sm">
      <p class="text-[var(--muted)]">Quick reference for getting around the app. Most flows live on a single screen — use the sidebar to switch sections, and the gear (⚙) for one-off settings.</p>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Sidebar &amp; navigation</h4>
        <ul class="list-disc pl-5 space-y-1">
          <li><b>☰</b> top-left: toggle the sidebar open/closed (auto-collapses on small screens)</li>
          <li><b>Section name</b> in the sidebar jumps to that view; the URL hash updates so refresh keeps you put</li>
          <li><b>Primary action</b> button (top-right when a section has one): opens the right Add form for that section</li>
          <li><b>⚙ Configs</b>: Defaults, Dropdown Lists, Tax Bundle, Data Management</li>
          <li><b>Username chip</b>: Preferences, Help, About, Send Feedback, Sign out</li>
        </ul>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Search &amp; filters</h4>
        <ul class="list-disc pl-5 space-y-1">
          <li>Search boxes are <b>live</b> — results update as you type; no Enter needed</li>
          <li>Filters (Game, Status, Class, dates, etc.) combine with AND logic; <b>Clear</b> wipes them all</li>
          <li><b>🖨 Export</b> on filter bars: respects the current filters; opens a format/detail modal then PDF or CSV</li>
        </ul>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Lot history</h4>
        <ul class="list-disc pl-5 space-y-1">
          <li>Any ID rendered in the cyan monospace font is clickable — opens the Lot History modal</li>
          <li>Bulk pool tiles above the Cards table are clickable — see every event that touched the pool</li>
          <li>Inside Lot History, sub-IDs are also clickable for further drill-down</li>
        </ul>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Forms</h4>
        <ul class="list-disc pl-5 space-y-1">
          <li>Multi-item forms (Sale, Purchase, Trade, Grading, Rip) accept <b>+ Bulk</b> rows for bulk inventory</li>
          <li><b>⌕ Lookup</b> on card forms opens the Pokémon TCG quick-picker (image, set, FMV auto-fill)</li>
          <li>Set fields are autocomplete combo-boxes per game; first use of each game triggers a cache fetch</li>
          <li>Sources (purchase / adjustment / rip / trade IDs) link the lot back to its origin; opening Lot History walks that link</li>
        </ul>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Cloud &amp; data</h4>
        <ul class="list-disc pl-5 space-y-1">
          <li>Every change saves to the cloud automatically — no Save All button needed</li>
          <li><b>Data Management</b> (⚙ menu): backup JSON, import JSON, wipe everything, sweep receipts to Storage</li>
          <li><b>Tax Bundle</b> (⚙ menu): year-end PDF with sales, purchases, expenses, mileage, inventory snapshot</li>
        </ul>
      </div>
    </div>`;
  const footer = `<button class="btn btn-primary" onclick="closeModal()">Got it</button>`;
  openModal({ title: "Help", body, footer, wide: true });
}

/* ---------- About modal ---------- */

const APP_VERSION = "v0.3";

export function openAbout(): void {
  const buildDate = new Date().toLocaleDateString();
  const body = `
    <div class="space-y-4 text-sm">
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-400 grid place-items-center font-bold text-white text-2xl">E</div>
        <div>
          <div class="font-semibold text-base">Enigma Ledger</div>
          <div class="text-xs text-[var(--muted)]">Cloud inventory ${escapeHtml(APP_VERSION)} &middot; viewed ${escapeHtml(buildDate)}</div>
        </div>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">What this app does</h4>
        <p class="text-sm">A Pokémon TCG (and other TCG) inventory tracker for resellers running solo. Covers singles, slabs, sealed product, bulk pools, ripping/grading/trading, multi-item sales, expenses, mileage, and year-end tax reporting.</p>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Recent additions</h4>
        <ul class="list-disc pl-5 space-y-1 text-sm">
          <li>Bulk pool system across rip / purchase / trade / sale with audit-trail Lot History</li>
          <li>Inventory PDF / CSV export with compact and detailed views</li>
          <li>Sales &amp; Purchases reports with date-range filtering</li>
          <li>One-click year-end tax bundle PDF</li>
          <li>Username dropdown with Preferences, Help, About</li>
        </ul>
      </div>

      <div class="panel-2 p-3">
        <h4 class="font-semibold mb-2 text-emerald-300">Tech</h4>
        <p class="text-sm">Vite + TypeScript + Tailwind on the front; Supabase Postgres + Auth + Storage on the back; deployed via Cloudflare Pages.</p>
      </div>
    </div>`;
  const footer = `<button class="btn btn-primary" onclick="closeModal()">Close</button>`;
  openModal({ title: "About Enigma Ledger", body, footer });
}

/* ---------- Send Feedback modal ---------- */

const FEEDBACK_EMAIL = "paul.riddle13@gmail.com";

export function openFeedback(): void {
  const body = `
    <div class="space-y-3">
      <p class="text-sm text-[var(--muted)]">Capture a thought, bug, or wish about the app. The Send button below pre-fills your default email client with the message; you can then send it to yourself for follow-up, or to whoever maintains the app.</p>
      <div>
        <label class="label" for="feedbackSubject">Subject</label>
        <input class="input" id="feedbackSubject" value="Enigma Ledger feedback" placeholder="One-line summary">
      </div>
      <div>
        <label class="label" for="feedbackBody">Details</label>
        <textarea class="input" id="feedbackBody" rows="6" placeholder="What's the issue, idea, or wish? Steps to reproduce if it's a bug."></textarea>
      </div>
      <div class="text-xs text-[var(--muted)]">Recipient: <code>${escapeHtml(FEEDBACK_EMAIL)}</code></div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="sendFeedback()">Open in email</button>`;
  openModal({ title: "Send Feedback", body, footer });
}

function sendFeedback(): void {
  const subject = ($("#feedbackSubject") as HTMLInputElement | null)?.value || "Enigma Ledger feedback";
  const bodyTxt = ($("#feedbackBody")    as HTMLTextAreaElement | null)?.value || "";
  if (!bodyTxt.trim()) {
    toast("Add a few words about the feedback first.", "err");
    return;
  }
  const mailto = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyTxt)}`;
  window.location.href = mailto;
  closeModal();
  toast("Opening your email client…");
}

/* ---------- Change password modal ---------- */

/** Reusable inline-SVG markup for an "eye" / "eye with slash" password toggle.
 *  The two SVGs sit on top of each other; the toggle script flips which one is
 *  visible based on the input's current `type`. */
const PW_TOGGLE_HTML = (inputId: string) => `
  <button type="button" class="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-white p-1" data-pw-toggle="${inputId}" title="Show password" aria-label="Show password">
    <svg data-pw-show width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
    <svg data-pw-hide width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  </button>`;

export function openChangePassword(): void {
  const body = `
    <div class="space-y-3">
      <p class="text-sm text-[var(--muted)]">Verify your current password, then set a new one.</p>
      <div id="pwErr" class="hidden panel-2 !border-rose-500/50 text-rose-300 p-2 text-xs"></div>
      <div>
        <label class="label" for="pwCurrent">Current password</label>
        <div class="relative">
          <input id="pwCurrent" type="password" class="input pr-10" autocomplete="current-password" required />
          ${PW_TOGGLE_HTML("pwCurrent")}
        </div>
      </div>
      <div>
        <label class="label" for="pwNew">New password</label>
        <div class="relative">
          <input id="pwNew" type="password" class="input pr-10" autocomplete="new-password" required minlength="6" />
          ${PW_TOGGLE_HTML("pwNew")}
        </div>
      </div>
      <div>
        <label class="label" for="pwNew2">Confirm new password</label>
        <div class="relative">
          <input id="pwNew2" type="password" class="input pr-10" autocomplete="new-password" required minlength="6" />
          ${PW_TOGGLE_HTML("pwNew2")}
        </div>
      </div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" id="btnCommitChangePw">Change password</button>`;
  openModal({ title: "Change password", body, footer });

  document.querySelectorAll<HTMLElement>("[data-pw-toggle]").forEach(btn => {
    btn.addEventListener("click", () => {
      const id = btn.getAttribute("data-pw-toggle")!;
      const input = document.getElementById(id) as HTMLInputElement | null;
      const show = btn.querySelector<SVGElement>("[data-pw-show]");
      const hide = btn.querySelector<SVGElement>("[data-pw-hide]");
      if (!input || !show || !hide) return;
      const willShow = input.type === "password";
      input.type = willShow ? "text" : "password";
      show.style.display = willShow ? "none" : "";
      hide.style.display = willShow ? "" : "none";
    });
  });
  document.getElementById("btnCommitChangePw")?.addEventListener("click", commitChangePassword);
}

function showPwError(msg: string): void {
  const el = document.getElementById("pwErr");
  if (!el) return;
  el.textContent = msg;
  el.classList.remove("hidden");
}

async function commitChangePassword(): Promise<void> {
  const cur   = ($("#pwCurrent") as HTMLInputElement | null)?.value || "";
  const next  = ($("#pwNew")     as HTMLInputElement | null)?.value || "";
  const next2 = ($("#pwNew2")    as HTMLInputElement | null)?.value || "";
  if (!cur)                     return showPwError("Enter your current password.");
  if (!next || next.length < 6) return showPwError("New password must be at least 6 characters.");
  if (next !== next2)           return showPwError("New passwords don't match.");
  if (next === cur)             return showPwError("New password must be different from the current one.");

  const email = CLOUD_USER?.email;
  if (!email) return showPwError("Not signed in. Reload and try again.");

  const verify = await sb.auth.signInWithPassword({ email, password: cur });
  if (verify.error) return showPwError("Current password is incorrect.");

  const upd = await sb.auth.updateUser({ password: next });
  if (upd.error) return showPwError(upd.error.message);

  toast("Password changed. Next sign-in will use the new password.");
  closeModal();
}

(window as any).openPreferences    = openPreferences;
(window as any).savePreferences    = savePreferences;
(window as any).openHelp           = openHelp;
(window as any).openAbout          = openAbout;
(window as any).openFeedback       = openFeedback;
(window as any).sendFeedback       = sendFeedback;
(window as any).openChangePassword = openChangePassword;
